import cloneDeep from 'clone-deep';

import { CatanView, DevelopmentCardKind, normaliseCatanView } from './catan-view';
import { GameEvent, EventType } from './events/event';
import { PlayerJoinEvent } from './events/player-join.event';
import { ChangeColourEvent } from './events/change-colour.event';
import { GameStartEvent } from './events/game-start.event';
import { PiecePlacedEvent } from './events/piece-placed.event';
import { TurnStartEvent } from './events/turn-start.event';
import { DiceRolledEvent } from './events/dice-rolled.event';
import { TradeOfferedEvent } from './events/trade-offered.event';
import { TradeAcceptedEvent } from './events/trade-accepted.event';
import { TradeCounteredEvent } from './events/trade-countered.event';
import { TradeConcludedEvent } from './events/trade-concluded.event';
import { TradeWithdrawnEvent } from './events/trade-withdrawn.event';
import { DiscardRequiredEvent } from './events/discard-required.event';
import { DiscardedEvent } from './events/discarded.event';
import { RobberRequiredEvent } from './events/robber-required.event';
import { RobberMovedEvent } from './events/robber-moved.event';
import { TurnResumedEvent } from './events/turn-resumed.event';
import { PieceBuiltEvent } from './events/piece-built.event';
import { DevelopmentCardBoughtEvent } from './events/development-card-bought.event';
import { BankTradedEvent } from './events/bank-traded.event';
import { KnightPlayedEvent } from './events/knight-played.event';
import { DevelopmentCardPlayedEvent } from './events/development-card-played.event';
import { MonopolyPlayedEvent } from './events/monopoly-played.event';
import { WinEvent } from './events/win.event';
import { ResourceType } from './board/board-view';
import { CatanState, StateType, TurnState } from './state/state';

export function updateCatan(game: CatanView, event: GameEvent): CatanView {
  const updated = cloneDeep(game);

  updated.lockState = event.lockState;

  switch (event.eventType) {
    case EventType.PlayerJoin:
      handleJoin(updated, event);
      break;
    case EventType.ChangeColour:
      handleChangeColour(updated, event);
      break;
    case EventType.GameStart:
      handleGameStart(updated, event);
      break;
    case EventType.PiecePlaced:
      handlePiecePlaced(updated, event);
      break;
    case EventType.TurnStart:
      handleTurnStart(updated, event);
      break;
    case EventType.DiceRolled:
      handleDiceRolled(updated, event);
      break;
    case EventType.TradeOffered:
      handleTradeOffered(updated, event);
      break;
    case EventType.TradeAccepted:
      handleTradeAccepted(updated, event);
      break;
    case EventType.TradeCountered:
      handleTradeCountered(updated, event);
      break;
    case EventType.TradeConcluded:
      handleTradeConcluded(updated, event);
      break;
    case EventType.TradeWithdrawn:
      handleTradeWithdrawn(updated, event);
      break;
    case EventType.DiscardRequired:
      handleDiscardRequired(updated, event);
      break;
    case EventType.Discarded:
      handleDiscarded(updated, event);
      break;
    case EventType.RobberRequired:
      handleRobberRequired(updated, event);
      break;
    case EventType.RobberMoved:
      handleRobberMoved(updated, event);
      break;
    case EventType.TurnResumed:
      handleTurnResumed(updated, event);
      break;
    case EventType.PieceBuilt:
      handlePieceBuilt(updated, event);
      break;
    case EventType.DevelopmentCardBought:
      handleDevelopmentCardBought(updated, event);
      break;
    case EventType.KnightPlayed:
      handleKnightPlayed(updated, event);
      break;
    case EventType.BankTraded:
      handleBankTraded(updated, event);
      break;
    case EventType.DevelopmentCardPlayed:
      handleDevelopmentCardPlayed(updated, event);
      break;
    case EventType.MonopolyPlayed:
      handleMonopolyPlayed(updated, event);
      break;
    case EventType.Win:
      handleWin(updated, event);
      break;
  }

  return normaliseCatanView(updated);
}

function handleJoin(updated: CatanView, event: PlayerJoinEvent) {
  updated.players[event.data.playerIndex] = event.data.player;

  // The view names every player's score from the first look, so a seat that is
  // already open learns of a joiner through this event alone: without the entry
  // the reduced state carries one score fewer than the server's, and the state
  // check in the hub reports the mismatch on every seat that stayed open.
  if (!updated.points.some((entry) => entry.playerId === event.data.player.id)) {
    updated.points.push({ playerId: event.data.player.id, points: 0 });
  }
}

function handleChangeColour(updated: CatanView, event: ChangeColourEvent) {
  const player = updated.players.find((p) => p.id === event.data.player);
  if (player) {
    player.colour = event.data.colour;
  }
}

function handleGameStart(updated: CatanView, event: GameStartEvent) {
  const turnOrder = event.data.turnOrder;
  const currentPlayerId = turnOrder[0] ?? null;
  const mine = currentPlayerId === updated.accessingPlayerId;
  updated.state = {
    state: StateType.Setup,
    turnOrder,
    stepIndex: 0,
    currentPlayerId,
    piece: 'Settlement',
    remainingPlacements: turnOrder.length * 4,
    legalVertexIds: mine ? event.data.legalVertexIds : [],
    legalEdgeIds: mine ? event.data.legalEdgeIds : [],
  };
  updated.topology = event.data.topology;
  updated.layout = event.data.layout;
  // The robber starts on the desert, and the server's view names that cell from
  // the first look at the board; the start event carries the layout it is read from.
  updated.robberCellId =
    event.data.layout.cells.find((cell) => cell.resource === 'Desert')?.cellId ?? null;
  updated.settlements = [];
  updated.cities = [];
  updated.roads = [];
  // A fresh board is worth nothing, and the server's view says so for every
  // player rather than leaving them out: starting the score here keeps the two
  // in step from the first look at the board.
  updated.points = updated.players.map((player) => ({
    playerId: player.id,
    points: 0,
  }));
  // A fresh board has no route on it, so nobody holds the award yet.
  updated.longestRoadPlayerId = null;
  // Nor has anyone played a knight, so nobody holds the army either.
  updated.largestArmyPlayerId = null;
}

function handlePiecePlaced(updated: CatanView, event: PiecePlacedEvent) {
  const {
    playerId,
    piece,
    locationId,
    stepIndex,
    currentPlayerId,
    nextPiece,
    remainingPlacements,
    victoryPoints,
  } = event.data;

  if (piece === 'Settlement') {
    updated.settlements.push({ vertexId: locationId, playerId });
  } else {
    updated.roads.push({ edgeId: locationId, playerId });
  }

  // The second settlement pays the cells it touches, and the event carries what
  // it paid, so the counts move here instead of waiting for a reload. Guarded
  // because the web and the API deploy independently, and an older API sends no
  // payout with this event at all.
  (event.data.paidResources ?? []).forEach((amount) =>
    gain(updated, playerId, amount.resource, amount.count),
  );

  // A settlement is worth a point and this placement is where it lands, so the
  // score the server already holds arrives with the placement instead of at the
  // next reload. Guarded: an API that predates the field sends none, and a score
  // that is absent must be left alone rather than reset to nothing.
  applyLongestRoad(updated, event.data.longestRoadPlayerId);
  if (typeof victoryPoints === 'number') {
    setPoints(updated, playerId, victoryPoints);
  }

  if (updated.state.state === StateType.Setup) {
    const mine = currentPlayerId !== null && currentPlayerId === updated.accessingPlayerId;
    updated.state = {
      ...updated.state,
      stepIndex,
      currentPlayerId,
      piece: nextPiece,
      remainingPlacements,
      legalVertexIds: mine ? event.data.legalVertexIds : [],
      legalEdgeIds: mine ? event.data.legalEdgeIds : [],
    };
  }
}

/**
 * The development-card plays the viewer is offered, derived from the private
 * hand it holds. Which cards a player may play is a per-seat answer about a
 * secret (ADR-0002), so the broadcast turn start, resumed turn and build events
 * carry none of it; each seat works its own out from the hand it holds, and the
 * server's per-player view reports the same answer. A client reducing one of
 * those events fills the flags here so the reduced state still equals the view.
 */
function developmentCardPlays(state: TurnState, view: CatanView) {
  const mine =
    state.currentPlayerId === view.accessingPlayerId &&
    !(state.developmentCardPlayed ?? false);

  const holds = (card: DevelopmentCardKind): boolean =>
    (view.developmentCards ?? []).some(
      (held) =>
        held.playerId === view.accessingPlayerId &&
        held.card === card &&
        !held.boughtThisTurn,
    );

  // The other three wait for the free roads of a road building card, which the
  // server holds the turn on until they are down; a knight does not (the
  // server's hold never names it), which is why `canPlayKnight` in
  // `catan.helpers` takes the three facts above and no more.
  const holdingTurn = mine && (state.freeRoadsRemaining ?? 0) === 0;

  return {
    canPlayRoadBuilding:
      holdingTurn &&
      holds('RoadBuilding') &&
      (state.legalRoadEdgeIds ?? []).length > 0,
    canPlayMonopoly: holdingTurn && holds('Monopoly'),
    canPlayYearOfPlenty: holdingTurn && holds('YearOfPlenty'),
  };
}

/** The play phase begins: setup's last road, or the end of the turn before. */
function handleTurnStart(updated: CatanView, event: TurnStartEvent) {
  const currentPlayerId = event.data.player;
  const mine = currentPlayerId === updated.accessingPlayerId;

  // A fresh turn has played no card, and no card in anyone's hand was bought
  // in it yet: the turn that ended took its new-card marks with it. The marks
  // come off before the plays are worked out, so a card bought in the turn that
  // just ended is playable in this one.
  updated.developmentCards = updated.developmentCards.map((held) => ({
    ...held,
    boughtThisTurn: false,
  }));

  const next: TurnState = {
    state: StateType.Turn,
    turnOrder: announcedTurnOrder(updated.state),
    turnIndex: event.data.turnIndex,
    currentPlayerId,
    dice: [],
    production: [],
    // A trade belongs to the turn that made it, and this is the event that
    // ends that turn: the offer and every acceptance of it are gone.
    offer: null,
    acceptances: [],
    counters: [],
    legalRoadEdgeIds: mine ? event.data.legalRoadEdgeIds ?? [] : [],
    legalSettlementVertexIds: mine
      ? event.data.legalSettlementVertexIds ?? []
      : [],
    legalCityVertexIds: mine ? event.data.legalCityVertexIds ?? [] : [],
    // A fresh turn has its buy to make, but only the server knows whether this
    // player still can: the flag it reports is what the board offers the buy on.
    canBuyDevelopmentCard: mine
      ? event.data.canBuyDevelopmentCard ?? false
      : false,
    developmentCardPlayed: false,
    // A turn that has only just begun owes no free roads.
    freeRoadsRemaining: 0,
    bankTradeRates: mine ? event.data.bankTradeRates ?? [] : [],
  };

  // The development-card plays are not on the event: a hand is private and the
  // event is broadcast, so this seat works them out from the cards it holds.
  updated.state = { ...next, ...developmentCardPlays(next, updated) };
}

/**
 * A piece is built during a turn. The build moves the builder's counts — the
 * event carries what it took out of their hand — puts the piece on the board,
 * a city replacing the settlement it upgrades, and reports the builder's points
 * afterwards, so the whole table reads the same board as the server without a
 * reload. It also carries what the builder may build on next, which replaces the
 * targets the board is offering them.
 */
function handlePieceBuilt(updated: CatanView, event: PieceBuiltEvent) {
  const {
    playerId,
    piece,
    locationId,
    spentResources,
    victoryPoints,
    legalRoadEdgeIds,
    legalSettlementVertexIds,
    legalCityVertexIds,
    bankTradeRates,
  } = event.data;

  if (piece === 'Road') {
    updated.roads.push({ edgeId: locationId, playerId });
  } else if (piece === 'Settlement') {
    updated.settlements.push({ vertexId: locationId, playerId });
  } else {
    // The city supersedes the settlement on its vertex, so the board carries
    // one piece there and pays two.
    updated.settlements = updated.settlements.filter(
      (settlement) => settlement.vertexId !== locationId,
    );
    updated.cities.push({ vertexId: locationId, playerId });
  }

  // The cost is public — a fixed rule and a public piece — so the event carries
  // it and the board moves what the server already moved: the builder's own
  // composition, for whoever is reading their own hand, and every player's
  // count. An API that predates the field sends no cost at all, so nothing
  // moves then.
  (spentResources ?? []).forEach((amount) => {
    lose(updated, playerId, amount.resource, amount.count);
    countCards(updated, playerId, -amount.count);
  });

  // The award moves with the build, before the builder's own total lands: the
  // seat that lost it drops two and the seat that took it gains two, and the
  // builder's authoritative total (which already includes the award when they
  // hold it) overwrites its own entry below.
  applyLongestRoad(updated, event.data.longestRoadPlayerId);

  // The event's total is the public one. The reader's own entry also counts the
  // victory point cards only they can see — the view adds those, and no event
  // carries them — so a build by their holder must not drop their own score.
  setPoints(
    updated,
    playerId,
    (victoryPoints ?? 0) +
      (updated.accessingPlayerId === playerId ? hiddenVictoryPoints(updated) : 0),
  );

  if (updated.state.state === StateType.Turn) {
    const mine = updated.state.currentPlayerId === updated.accessingPlayerId;
    const next: TurnState = {
      ...updated.state,
      legalRoadEdgeIds: mine ? legalRoadEdgeIds ?? [] : [],
      legalSettlementVertexIds: mine
        ? legalSettlementVertexIds ?? []
        : [],
      legalCityVertexIds: mine ? legalCityVertexIds ?? [] : [],
      bankTradeRates: mine ? bankTradeRates ?? [] : [],
      // A free road counts the card's roads down; an API that predates the
      // field says nothing, which leaves the count alone.
      freeRoadsRemaining:
        event.data.freeRoadsRemaining ?? updated.state.freeRoadsRemaining ?? 0,
    };

    // A build spends the hand and can use up the last road piece or the last
    // open edge, so the plays the private hand offers are worked out again —
    // the event carries none of them, since it reaches the whole table.
    updated.state = { ...next, ...developmentCardPlays(next, updated) };
  }
}

/**
 * A development card is played. A played card is face up, so the event names
 * the kind: the count of cards the player holds moves for everyone, and the
 * player's own list loses a card of that kind. The play is the turn's one, so
 * nobody is offered another, and a road building card holds the turn on its free
 * roads — the first of which may go where the event says, for the player placing
 * them.
 */
function handleDevelopmentCardPlayed(
  updated: CatanView,
  event: DevelopmentCardPlayedEvent,
) {
  const {
    player: playerId,
    card,
    developmentCardCount,
    resourcesTaken,
  } = event.data;
  const player = updated.players.find((p) => p.id === playerId);

  if (player) {
    player.developmentCardCount = developmentCardCount;
  }

  if (playerId === updated.accessingPlayerId) {
    // The card played is one bought in an earlier turn, as the server picks it.
    const index = updated.developmentCards.findIndex(
      (held) =>
        held.playerId === playerId && held.card === card && !held.boughtThisTurn,
    );
    if (index >= 0) {
      updated.developmentCards.splice(index, 1);
    }
  }

  // A year of plenty card hands over the two resources it names, which are
  // public: the count moves for everyone and the composition for the holder.
  (resourcesTaken ?? []).forEach((taken) => {
    gain(updated, playerId, taken.resource, taken.count);
  });

  if (updated.state.state === StateType.Turn) {
    const mine = playerId === updated.accessingPlayerId;
    // Only a road building card owes free roads and names where the first may
    // go; any other card leaves the places the player may build as they were.
    const owesRoads = event.data.freeRoadsRemaining > 0;
    updated.state = {
      ...updated.state,
      canPlayRoadBuilding: false,
      canPlayMonopoly: false,
      // One card a turn: a played card, of whatever kind, uses it up.
      developmentCardPlayed: true,
      canPlayYearOfPlenty: false,
      freeRoadsRemaining: event.data.freeRoadsRemaining,
      legalRoadEdgeIds: owesRoads
        ? mine
          ? event.data.legalRoadEdgeIds
          : []
        : updated.state.legalRoadEdgeIds,
    };
  }
}

/**
 * A monopoly card is played. The card is face up, so the kind and the resource
 * are public: the player's own list loses the card, the player's own hand gains
 * the cards that arrived, and every other hand loses the whole of that resource
 * — which only its holder can see. Each seat's count then moves to the count the
 * event reports, the number the board already shows. The play is the turn's one,
 * so nobody is offered another.
 */
function handleMonopolyPlayed(updated: CatanView, event: MonopolyPlayedEvent) {
  const {
    player: playerId,
    amount,
    developmentCardCount,
    playerCardCount,
    victims,
  } = event.data;
  const resource = event.data.resource as ResourceType;
  const mine = playerId === updated.accessingPlayerId;
  const player = updated.players.find((p) => p.id === playerId);

  if (player) {
    player.developmentCardCount = developmentCardCount;
    player.cardCount = playerCardCount;
  }

  victims.forEach((victim) => {
    const row = updated.players.find((p) => p.id === victim.playerId);
    if (row) {
      row.cardCount = victim.cardCount;
    }
  });

  if (mine) {
    const index = updated.developmentCards.findIndex(
      (held) => held.playerId === playerId && held.card === 'Monopoly',
    );
    if (index >= 0) {
      updated.developmentCards.splice(index, 1);
    }

    // The whole of the resource moved to this hand, so it holds the rest of the
    // amount it already had plus every copy that arrived.
    if (amount > 0) {
      const holding = updated.resources.find(
        (held) => held.playerId === playerId && held.resource === resource,
      );
      if (holding) {
        holding.count += amount;
      } else {
        updated.resources.push({ playerId, resource, count: amount });
      }
    }
  } else {
    // A victim lost every copy; a seat that held none finds nothing to remove.
    updated.resources = updated.resources.filter(
      (held) => !(held.playerId === updated.accessingPlayerId && held.resource === resource),
    );
  }

  if (updated.state.state === StateType.Turn) {
    updated.state = {
      ...updated.state,
      canPlayRoadBuilding: false,
      canPlayMonopoly: false,
      canPlayYearOfPlenty: false,
      // One card a turn: a played card, of whatever kind, uses it up.
      developmentCardPlayed: true,
    };
  }
}

/**
 * A development card is bought. The count is public and moves here; the kind is
 * not in the event, which reaches the whole table, so the buyer learns what
 * they drew only from the view they re-read when the refresh follows. The one
 * buy a turn allows is spent, so no seat is offered another.
 */
function handleDevelopmentCardBought(
  updated: CatanView,
  event: DevelopmentCardBoughtEvent,
) {
  const player = updated.players.find((p) => p.id === event.data.player);

  if (player) {
    player.developmentCardCount = event.data.developmentCardCount;
  }

  if (updated.state.state === StateType.Turn) {
    updated.state = { ...updated.state, canBuyDevelopmentCard: false };
  }
}

/**
 * A knight is played: it is counted for its owner, leaves their hand and can take
 * the largest army for them. The hand's own cards arrive with the refresh that
 * ends the stream, since only the owner may know which card went; the count of
 * cards and the army's holder are public, and the event carries both.
 */
function handleKnightPlayed(updated: CatanView, event: KnightPlayedEvent) {
  const {
    player: playerId,
    knightsPlayed,
    developmentCardCount,
    victoryPoints,
    largestArmyPlayerId,
  } = event.data;
  const player = updated.players.find((p) => p.id === playerId);

  if (player) {
    player.knightsPlayed = knightsPlayed;
    player.developmentCardCount = developmentCardCount;
  }

  // The award moves with the play: whoever the server names holds it and its two
  // points, and the seat that lost it drops them.
  applyLargestArmy(updated, largestArmyPlayerId);

  // The event's total is the public one. The reader's own entry also counts the
  // victory point cards only they can see, so their own play must not drop them.
  if (typeof victoryPoints === 'number') {
    setPoints(
      updated,
      playerId,
      victoryPoints +
        (updated.accessingPlayerId === playerId ? hiddenVictoryPoints(updated) : 0),
    );
  }
}

/**
 * The game is over: a player reached ten points. The win carries the winner, the
 * reason, every player's final score and the cards behind the win, which is
 * where the winner's hidden victory point cards are revealed — the event reaches
 * the whole table, so the reveal travels in it rather than waiting for a reload.
 * The terminal state leaves nothing more to play.
 *
 * The event's scores are the totals everyone can work out from the board: a
 * hidden victory point card counts in its owner's own total and in nobody
 * else's, and the seat the event was built from is the winner's, whose cards
 * the win itself revealed. So the one row that can read differently is this
 * seat's own, and this seat's own hidden cards are added back to it — the same
 * correction a points event gets, applied to the scores the win is read from.
 */
function handleWin(updated: CatanView, event: WinEvent) {
  const hidden = hiddenVictoryPoints(updated);
  const scores =
    event.data.winnerId === updated.accessingPlayerId || hidden === 0
      ? event.data.scores
      : event.data.scores.map((score) =>
          score.playerId === updated.accessingPlayerId
            ? { ...score, points: score.points + hidden }
            : score,
        );

  updated.state = {
    state: StateType.Win,
    winnerId: event.data.winnerId,
    reason: event.data.reason,
    scores,
    // The event names the same set `cards`; the state carries the view's name
    // for it, so a win read off the event and a win read off a reload agree.
    developmentCards: event.data.cards,
  };
  // The revealed scores are the game-level ones too, so the board's player list
  // shows every final total, the winner's cards included.
  updated.points = scores;
  // The winner's held cards become visible to every seat, as the server's view
  // has it: the revealed set replaces any this seat already held for the winner
  // and this seat's own cards stay as they are, so a non-winner's list gains
  // exactly the winner's cards and its own.
  const others = updated.developmentCards.filter(
    (held) => held.playerId !== event.data.winnerId,
  );
  updated.developmentCards = [...others, ...event.data.cards];
}

function handleDiceRolled(updated: CatanView, event: DiceRolledEvent) {
  if (updated.state.state !== StateType.Turn) {
    return;
  }

  updated.state = {
    ...updated.state,
    dice: event.data.dice,
    production: event.data.production,
  };

  // The event carries what each player's pieces paid, so the counts the board
  // shows move by the same amounts the server has already stored.
  event.data.production.forEach((produced) =>
    produced.resources.forEach((amount) =>
      gain(updated, produced.playerId, amount.resource, amount.count),
    ),
  );

  offerBuyOnceAffordable(updated);
}

/** What a development card costs: one ore, one wheat and one sheep. */
const DEVELOPMENT_CARD_COST = [
  ResourceType.Ore,
  ResourceType.Wheat,
  ResourceType.Sheep,
];

/**
 * The roll can pay for the card the turn began too poor to buy, and the server's
 * view then offers the buy. The flag the turn start reported is stale by then,
 * so a seat that holds the cost and has bought no card this turn is offered it.
 * A card it bought is on the hand with its new-card mark; the deck running dry
 * is the one answer left to the server's refresh.
 */
function offerBuyOnceAffordable(updated: CatanView) {
  const state = updated.state;
  if (
    state.state !== StateType.Turn ||
    state.currentPlayerId !== updated.accessingPlayerId ||
    state.canBuyDevelopmentCard
  ) {
    return;
  }

  const held = (resource: ResourceType) =>
    updated.resources.find(
      (holding) =>
        holding.playerId === updated.accessingPlayerId &&
        holding.resource === resource,
    )?.count ?? 0;
  const bought = updated.developmentCards.some(
    (card) =>
      card.playerId === updated.accessingPlayerId && card.boughtThisTurn,
  );

  if (!bought && DEVELOPMENT_CARD_COST.every((resource) => held(resource) >= 1)) {
    updated.state = { ...state, canBuyDevelopmentCard: true };
  }
}

/** A seven caught players over the hand limit: the game waits for all of them. */
function handleDiscardRequired(updated: CatanView, event: DiscardRequiredEvent) {
  const state = updated.state;
  const dice = state.state === StateType.Turn ? state.dice : [];

  updated.state = {
    state: StateType.Discard,
    turnOrder: announcedTurnOrder(state),
    turnIndex: state.state === StateType.Turn ? state.turnIndex : 0,
    currentPlayerId: state.state === StateType.Turn ? state.currentPlayerId : null,
    dice,
    pendingPlayerIds: event.data.players.map((player) => player.playerId),
    cardsToDiscard:
      event.data.players.find((player) => player.playerId === updated.accessingPlayerId)
        ?.cards ?? 0,
  };
}

function handleDiscarded(updated: CatanView, event: DiscardedEvent) {
  if (updated.state.state !== StateType.Discard) {
    return;
  }

  const total = event.data.resources.reduce((count, amount) => count + amount.count, 0);
  event.data.resources.forEach((amount) =>
    lose(updated, event.data.player, amount.resource, amount.count),
  );
  countCards(updated, event.data.player, -total);

  updated.state = {
    ...updated.state,
    pendingPlayerIds: updated.state.pendingPlayerIds.filter(
      (playerId) => playerId !== event.data.player,
    ),
  };
}

/** The discards are done, or the seven caught nobody: the robber is due. */
function handleRobberRequired(updated: CatanView, event: RobberRequiredEvent) {
  const state = updated.state;
  const dice = state.state === StateType.Turn || state.state === StateType.Discard
    ? state.dice
    : [];

  updated.state = {
    state: StateType.Robber,
    turnOrder: announcedTurnOrder(state),
    turnIndex:
      state.state === StateType.Turn || state.state === StateType.Discard
        ? state.turnIndex
        : 0,
    currentPlayerId: event.data.player,
    dice,
    robberCellId: updated.robberCellId ?? null,
    cells: event.data.cells ?? [],
  };
}

function handleRobberMoved(updated: CatanView, event: RobberMovedEvent) {
  updated.robberCellId = event.data.cellId;
}

/** The robber has moved: the interrupted turn takes over again. */
function handleTurnResumed(updated: CatanView, event: TurnResumedEvent) {
  const currentPlayerId = event.data.player;
  const mine = currentPlayerId === updated.accessingPlayerId;

  const next: TurnState = {
    state: StateType.Turn,
    turnOrder: announcedTurnOrder(updated.state),
    turnIndex: event.data.turnIndex,
    currentPlayerId,
    dice: event.data.dice,
    production: [],
    // The server's TurnStateView always carries both, so the reduced state must
    // too: null when no offer stands and a list (often empty) of acceptances.
    // The seven rebuilds the turn from the robber state, so the offer it
    // interrupted does not survive it — do not "restore" the old offer here.
    offer: null,
    acceptances: [],
    counters: [],
    // The seven interrupted the turn rather than ending it, so the resumed
    // player still has the places they may build: the event carries them just
    // as a turn start does, and the same guard keeps them off other boards.
    legalRoadEdgeIds: mine ? event.data.legalRoadEdgeIds ?? [] : [],
    legalSettlementVertexIds: mine
      ? event.data.legalSettlementVertexIds ?? []
      : [],
    legalCityVertexIds: mine ? event.data.legalCityVertexIds ?? [] : [],
    // The seven interrupted the turn rather than ending it, so the buy it had
    // already made stays made: the resumed flag is the server's answer, not a
    // fresh turn's.
    canBuyDevelopmentCard: mine
      ? event.data.canBuyDevelopmentCard ?? false
      : false,
    // A knight played in this turn is why the robber moved: the card is spent
    // for the turn, and the event says so because the turn is rebuilt here.
    developmentCardPlayed: event.data.developmentCardPlayed ?? false,
    // A seven cannot land while free roads are owed, so none are owed here.
    freeRoadsRemaining: 0,
    bankTradeRates: mine ? event.data.bankTradeRates ?? [] : [],
  };

  // The resumed player's cards are its own secret and every seat reads this
  // event, so the plays it may make are worked out from its hand here, not
  // carried.
  updated.state = { ...next, ...developmentCardPlays(next, updated) };
}

/**
 * A card arrives for a player. The count is public and always moves; the
 * composition only exists in the holder's own view, so for anyone else it must
 * not move at all — that is what keeps a robbery's card out of every other
 * player's reach.
 */
function gain(
  updated: CatanView,
  playerId: string,
  resource: ResourceType,
  count: number,
) {
  countCards(updated, playerId, count);

  if (playerId !== updated.accessingPlayerId) {
    return;
  }

  const holding = updated.resources.find(
    (held) => held.playerId === playerId && held.resource === resource,
  );

  if (holding) {
    holding.count += count;
    return;
  }

  updated.resources.push({ playerId, resource, count });
}

/** A card leaves a player's hand; the composition again only for its holder. */
function lose(
  updated: CatanView,
  playerId: string,
  resource: ResourceType,
  count: number,
) {
  if (playerId !== updated.accessingPlayerId) {
    return;
  }

  const index = updated.resources.findIndex(
    (held) => held.playerId === playerId && held.resource === resource,
  );

  if (index < 0) {
    return;
  }

  updated.resources[index].count -= count;

  if (updated.resources[index].count <= 0) {
    updated.resources.splice(index, 1);
  }
}

function countCards(updated: CatanView, playerId: string, count: number) {
  const player = updated.players.find((p) => p.id === playerId);

  if (player) {
    player.cardCount = (player.cardCount ?? 0) + count;
  }
}

/**
 * The victory point cards this seat can see, by kind. The server adds them to
 * that seat's own total in the view and announces only the public total in
 * events, so a client that reduces a build has to add them back itself. The list
 * also carries the winner's revealed cards once the game is won, so the count is
 * scoped to this seat's own: only they count towards a total that is still live.
 */
function hiddenVictoryPoints(updated: CatanView): number {
  return (updated.developmentCards ?? []).filter(
    (card) =>
      card.card === 'VictoryPoint' &&
      card.playerId === updated.accessingPlayerId,
  ).length;
}

/** One player's score from their pieces, public to the table like the rest. */
function setPoints(updated: CatanView, playerId: string, points: number) {
  const scored = updated.points.find((entry) => entry.playerId === playerId);

  if (scored) {
    scored.points = points;
    return;
  }

  updated.points.push({ playerId, points });
}

/** The two points a Catan award is worth to its holder. */
const AWARD_POINTS = 2;

/**
 * A Catan award moves to the holder the server names: the seat that lost it drops
 * two points, the seat that took it gains two, and the client remembers who holds
 * it. The longest road and the largest army move the same way and arrive in the
 * same pair of event fields, so they share one path. The announcing player's own
 * total overwrites its entry afterwards, so a player who takes the award does not
 * count the two points twice.
 */
function applyAward(
  updated: CatanView,
  award: 'longestRoad' | 'largestArmy',
  holderId?: string | null,
) {
  const holder = holderId ?? null;
  const previous =
    award === 'longestRoad'
      ? updated.longestRoadPlayerId ?? null
      : updated.largestArmyPlayerId ?? null;

  if (previous === holder) {
    return;
  }

  if (previous) {
    adjustPoints(updated, previous, -AWARD_POINTS);
  }

  if (holder) {
    adjustPoints(updated, holder, AWARD_POINTS);
  }

  if (award === 'longestRoad') {
    updated.longestRoadPlayerId = holder;
  } else {
    updated.largestArmyPlayerId = holder;
  }
}

/** The longest road award, moved by the build whose board change moved it. */
function applyLongestRoad(updated: CatanView, holderId?: string | null) {
  applyAward(updated, 'longestRoad', holderId);
}

/** The largest army award, moved by the knight whose play moved it. */
function applyLargestArmy(updated: CatanView, holderId?: string | null) {
  applyAward(updated, 'largestArmy', holderId);
}

/** Moves one player's published score, leaving a player the board has not met alone. */
function adjustPoints(updated: CatanView, playerId: string, delta: number) {
  const scored = updated.points.find((entry) => entry.playerId === playerId);

  if (scored) {
    scored.points += delta;
  }
}

function announcedTurnOrder(state: CatanState): string[] {
  return state.state === StateType.Setup ||
    state.state === StateType.Turn ||
    state.state === StateType.Discard ||
    state.state === StateType.Robber
    ? state.turnOrder
    : [];
}

/**
 * The active player's offer is on the table: the terms are public to everyone,
 * and the acceptances of the terms it replaced are gone — they accepted
 * something nobody is offering any more.
 */
function handleTradeOffered(updated: CatanView, event: TradeOfferedEvent) {
  if (updated.state.state !== StateType.Turn) {
    return;
  }

  updated.state = {
    ...updated.state,
    offer: {
      playerId: event.data.player,
      giving: event.data.giving ?? [],
      wanting: event.data.wanting ?? [],
    },
    acceptances: [],
    counters: [],
  };
}

/** A player's acceptance is public, and a player holds at most one. */
function handleTradeAccepted(updated: CatanView, event: TradeAcceptedEvent) {
  if (updated.state.state !== StateType.Turn) {
    return;
  }

  const others = (updated.state.acceptances ?? []).filter(
    (acceptance) => acceptance.playerId !== event.data.player,
  );

  updated.state = {
    ...updated.state,
    acceptances: [...others, { playerId: event.data.player }],
    // One position per player: an acceptance replaces a counter-offer.
    counters: (updated.state.counters ?? []).filter(
      (counter) => counter.playerId !== event.data.player,
    ),
  };
}

/**
 * A counter-offer is public with its own terms, and replaces whatever position
 * the player held: their acceptance, or an earlier counter-offer.
 */
function handleTradeCountered(updated: CatanView, event: TradeCounteredEvent) {
  if (updated.state.state !== StateType.Turn) {
    return;
  }

  updated.state = {
    ...updated.state,
    acceptances: (updated.state.acceptances ?? []).filter(
      (acceptance) => acceptance.playerId !== event.data.player,
    ),
    counters: [
      ...(updated.state.counters ?? []).filter(
        (counter) => counter.playerId !== event.data.player,
      ),
      {
        playerId: event.data.player,
        giving: event.data.giving ?? [],
        wanting: event.data.wanting ?? [],
      },
    ],
  };
}

/**
 * The trade concluded: exactly what was offered moves, each side's half to the
 * other, and the table is clear. The event carries both halves, so the counts
 * the board shows move here rather than waiting for a reload.
 */
function handleTradeConcluded(updated: CatanView, event: TradeConcludedEvent) {
  if (updated.state.state !== StateType.Turn) {
    return;
  }

  const { player, other } = event.data;

  // Each side's half goes to the other: the composition of the two hands that
  // may see it, and every player's count. gain moves the count with the card,
  // lose moves the composition alone, so the giver's count moves here.
  (event.data.giving ?? []).forEach((amount) => {
    lose(updated, player, amount.resource, amount.count);
    countCards(updated, player, -amount.count);
    gain(updated, other, amount.resource, amount.count);
  });

  (event.data.wanting ?? []).forEach((amount) => {
    lose(updated, other, amount.resource, amount.count);
    countCards(updated, other, -amount.count);
    gain(updated, player, amount.resource, amount.count);
  });

  updated.state = { ...updated.state, offer: null, acceptances: [], counters: [] };
}

/** The offer is withdrawn: the terms and every acceptance of them are gone. */
function handleTradeWithdrawn(updated: CatanView, event: TradeWithdrawnEvent) {
  if (updated.state.state !== StateType.Turn) {
    return;
  }

  updated.state = { ...updated.state, offer: null, acceptances: [], counters: [] };
}

/**
 * The bank trade: the swapper hands the bank the giving side and the bank hands
 * them the wanted one. Both amounts are public — a swap with the bank is made in
 * front of the table, unlike a card taken in a robbery — so the swapper's own
 * composition moves here and every player's count moves with it. The counts are
 * reduced wherever the seat is: a bank trade changes nothing a state could
 * invalidate.
 */
function handleBankTraded(updated: CatanView, event: BankTradedEvent) {
  const { playerId, giving, wanting } = event.data;

  lose(updated, playerId, giving.resource, giving.count);
  countCards(updated, playerId, -giving.count);
  gain(updated, playerId, wanting.resource, wanting.count);
}
